import { Button, Input } from '@blink/ui';
import { BrowserMultiFormatReader, type IScannerControls } from '@zxing/browser';
import { BarcodeFormat, DecodeHintType } from '@zxing/library';
import { ScannerInput, SCANNER_INPUT } from '@blink/shared';
import { Camera, CameraOff, ScanLine } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@/i18n';
import { unlockScanSound } from './scan-feedback';

export type ScanSource = 'CAMERA' | 'SCANNER' | 'TEST';

/** Local testing only (a PC has no camera): a typing box, built in only when VITE_SCAN_TEST_BOX=on. */
export const SCAN_TEST_BOX = import.meta.env.VITE_SCAN_TEST_BOX === 'on';

const hints = new Map([[DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.CODE_128, BarcodeFormat.CODE_39, BarcodeFormat.QR_CODE, BarcodeFormat.DATA_MATRIX, BarcodeFormat.EAN_13]]]);
/** the same label held in front of the camera is read many times a second; count it once */
const SAME_CODE_MS = 2500;

/** typing into a field (the local test box, a note) is never taken as a scan */
function typingInField(target: EventTarget | null) {
  const el = target as HTMLElement | null;
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable);
}

/**
 * Barcode scanner: the phone camera, or a PDA's built-in scanner (keyboard mode). Couriers cannot
 * type AWBs: scanner keystrokes are accepted only when they come as fast as a scanner sends them
 * (@blink/shared ScannerInput). Reads Code 128 labels printed by Blink and the clients' own codes.
 * The page gives the sound and vibration once the answer is known (scan-feedback.ts).
 */
export function Scanner({ onScan, busy }: { onScan: (code: string, source: ScanSource) => void; busy?: boolean }) {
  const { t } = useI18n();
  const video = useRef<HTMLVideoElement>(null);
  const controls = useRef<IScannerControls | null>(null);
  const last = useRef<{ code: string; at: number } | null>(null);
  const handler = useRef(onScan);
  handler.current = onScan;
  const [on, setOn] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [typed, setTyped] = useState('');

  const stop = () => {
    controls.current?.stop();
    controls.current = null;
    setOn(false);
  };

  const start = async () => {
    unlockScanSound();
    setProblem(null);
    try {
      const reader = new BrowserMultiFormatReader(hints, { delayBetweenScanAttempts: 150 });
      controls.current = await reader.decodeFromConstraints({ video: { facingMode: 'environment' } }, video.current!, (result) => {
        if (!result) return;
        const code = result.getText().trim();
        const now = Date.now();
        if (last.current && last.current.code === code && now - last.current.at < SAME_CODE_MS) return;
        last.current = { code, at: now };
        handler.current(code, 'CAMERA');
      });
      setOn(true);
    } catch (e) {
      setProblem((e as Error).name === 'NotAllowedError' ? t.scan.cameraBlocked : t.scan.noCamera);
      stop();
    }
  };

  useEffect(() => () => controls.current?.stop(), []);

  // PDA scanner: keystrokes anywhere on the page (no field has focus, so no keyboard opens)
  useEffect(() => {
    const input = new ScannerInput();
    let idle: ReturnType<typeof setTimeout> | undefined;
    const done = () => {
      const code = input.finish();
      if (!code) return;
      const now = Date.now();
      if (last.current && last.current.code === code && now - last.current.at < 1500) return;
      last.current = { code, at: now };
      handler.current(code, 'SCANNER');
    };
    const onKey = (e: KeyboardEvent) => {
      if (typingInField(e.target) || e.ctrlKey || e.altKey || e.metaKey) return;
      clearTimeout(idle);
      if (e.key === 'Enter' || e.key === 'Tab') {
        if (input.length) {
          e.preventDefault();
          done();
        }
        return;
      }
      if (e.key.length !== 1) return;
      input.char(e.key, e.timeStamp || performance.now());
      // scanners set to send no Enter: the code ends after a short quiet time
      idle = setTimeout(() => {
        if (input.length >= 6) done();
        else input.clear();
      }, SCANNER_INPUT.idleMs);
    };
    const unlock = () => unlockScanSound();
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', unlock, { once: true });
    return () => {
      clearTimeout(idle);
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', unlock);
    };
  }, []);

  return (
    <div className="space-y-2">
      <div className="relative overflow-hidden rounded-xl bg-black" style={{ aspectRatio: on ? '4 / 3' : undefined }}>
        <video ref={video} className={on ? 'h-full w-full object-cover' : 'hidden'} muted playsInline />
        {on ? <div className="pointer-events-none absolute inset-x-8 top-1/2 h-0.5 -translate-y-1/2 bg-red-500/80" /> : null}
      </div>
      {on ? (
        <Button variant="outline" className="h-12 w-full" onClick={stop} data-testid="camera-off">
          <CameraOff className="h-5 w-5" />
          {t.scan.stopCamera}
        </Button>
      ) : (
        <Button className="h-14 w-full text-base" onClick={() => void start()} data-testid="camera-on">
          <Camera className="h-5 w-5" />
          {t.scan.startCamera}
        </Button>
      )}
      {problem ? <p className="text-sm text-destructive">{problem}</p> : null}
      <p className="flex items-center justify-center gap-1.5 text-xs text-muted-foreground" data-testid="pda-ready">
        <ScanLine className="h-3.5 w-3.5" />
        {t.scan.pdaReady}
      </p>
      {SCAN_TEST_BOX ? (
        <form
          className="flex gap-2 rounded-lg border border-dashed border-warning p-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (!typed.trim() || busy) return;
            handler.current(typed.trim(), 'TEST');
            setTyped('');
          }}
        >
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={t.scan.testBox} aria-label={t.scan.testBox} className="h-10" data-testid="test-scan" autoCapitalize="characters" />
          <Button type="submit" variant="secondary" disabled={busy}>
            {t.scan.testGo}
          </Button>
        </form>
      ) : null}
    </div>
  );
}
